import React from 'react';
import usePage from '@dashboard/hooks/usePage';
import {
  ArrowRightIcon,
  ArrowTrendingUpIcon,
  CalendarIcon,
  ChartBarIcon,
  CheckIcon,
  EyeIcon,
  MapPinIcon,
  TicketIcon,
} from '@heroicons/react/24/outline';
import * as KPICard from '../components/KPICard';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import getConversionRate from '@dashboard/services/getConversionRate';
import RecentPayments from '../components/RecentPayments';
import { formatEventDate } from '@dashboard/lib/format-date';
import Link from '@dashboard/components/Link';
import { eventsAppUrl } from '@dashboard/services/env';
import Button from '@dashboard/components/Button';

export default function CheckoutOverview() {
  const { page } = usePage();
  const { eventDetails } = page;

  const conversionRate = getConversionRate(page.bookingCount, page.visitCount);

  const eventAppHref = `${eventsAppUrl}/event/${page._id}/guest-list`;

  return (
    <div>
      <div className="grid md:grid-cols-10 gap-x-4 gap-y-4 bg-white rounded py-8 px-4 border border-gray-100">
        <div className="col-span-3">
          <div className="text-2xl font-semibold flex gap-x-2">
            <div>{page.name}</div>
          </div>

          <div className="mt-2 space-y-1">
            {eventDetails.startDate && (
              <div className="flex items-start gap-x-2 justify-start">
                <div className="mt-0.5">
                  <CalendarIcon className="h-5 w-5" />
                </div>
                <div className="flex flex-col font-medium">
                  {formatEventDate({
                    startDate: eventDetails.startDate,
                    endDate: eventDetails.endDate,
                    timezone: eventDetails.timezone,
                  }).map(date => (
                    <span key={date}>{date}</span>
                  ))}
                </div>
              </div>
            )}

            {eventDetails.location && (
              <div className="flex items-center gap-x-2 justify-start">
                <div>
                  <MapPinIcon className="h-5 w-5" />
                </div>
                {eventDetails.mapsLink && (
                  <a
                    href={eventDetails.mapsLink}
                    target="_blank"
                    rel="nofollow noreferrer"
                    className="font-medium underline"
                  >
                    {eventDetails.location}
                  </a>
                )}

                {!eventDetails.mapsLink && (
                  <span className="font-medium">{eventDetails.location}</span>
                )}
              </div>
            )}

            <Button className="mt-2" appearance="secondary" target="_blank" href={eventAppHref}>
              <CheckIcon className="w-4 h-4 mr-2 align-baseline" />
              Check in guests
            </Button>
          </div>
        </div>

        <div className="col-span-7">
          <div className="grid md:grid-cols-4 gap-x-4 gap-y-4">
            <KPICard.Container>
              <KPICard.Icon icon={ChartBarIcon} />
              <KPICard.Header>
                <KPICard.Title>Revenue</KPICard.Title>
                <KPICard.InfoTooltip>Total revenue from bookings</KPICard.InfoTooltip>
              </KPICard.Header>
              <KPICard.Value>
                {getPriceLabel({
                  amount: page.bookingTotal,
                  currency: eventDetails.currency,
                })}
              </KPICard.Value>
            </KPICard.Container>

            <KPICard.Container>
              <KPICard.Icon icon={TicketIcon} />
              <KPICard.Header>
                <KPICard.Title>Bookings</KPICard.Title>
                <KPICard.InfoTooltip>Total number of bookings</KPICard.InfoTooltip>
              </KPICard.Header>
              <KPICard.Value>{page.bookingCount}</KPICard.Value>
            </KPICard.Container>

            <KPICard.Container>
              <KPICard.Icon icon={EyeIcon} />
              <KPICard.Header>
                <KPICard.Title>Page views</KPICard.Title>
                <KPICard.InfoTooltip>Total number of checkout views</KPICard.InfoTooltip>
              </KPICard.Header>
              <KPICard.Value>{page.visitCount}</KPICard.Value>
            </KPICard.Container>

            <KPICard.Container>
              <KPICard.Icon icon={ArrowTrendingUpIcon} />
              <KPICard.Header>
                <KPICard.Title>Conversion rate</KPICard.Title>
                <KPICard.InfoTooltip>
                  Conversion Rate (conversions ÷ views × 100)
                </KPICard.InfoTooltip>
              </KPICard.Header>
              <KPICard.Value>{conversionRate}%</KPICard.Value>
            </KPICard.Container>
          </div>
        </div>
      </div>

      <div className="mt-4 p-4 border border-gray-100 rounded bg-white">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-medium">Recent bookings</h2>

          <div>
            <Link
              href={`/pages/${page._id}/bookings`}
              className="flex items-center gap-x-1 whitespace-nowrap text-sm"
            >
              View all <ArrowRightIcon className="w-4 h-4" />
            </Link>
          </div>
        </div>
        <RecentPayments />
      </div>
    </div>
  );
}
